import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import BrowsingItem from 'molecules/BrowsingItem';
import FeaturedOffering from 'molecules/FeaturedOffering';
import OrdersList from 'molecules/OrdersList';
import Pagination from 'molecules/Pagination';
import dynamic from 'next/dynamic';
import React from 'react';
import { IOrder } from 'stores/networkInterfaces';
import { OfferingItem } from 'stores/offeringUtils';
import { IInvestment } from 'utils/globalTypes';
import { isNotEmpty } from 'utils/object';

import s from './BrowsingGalleryView.module.scss';

const BrowsingViewNoResults = dynamic(() => import('organisms/BrowsingViewNoResults'));

interface IBrowsingGalleryView {
  items?: Array<IInvestment>;
  promotion?: IInvestment;
  activeOrders?: Array<IOrder>;
  offset?: number;
  limit?: number;
  total?: number;
  showActions?: boolean;
  alignStart?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  onLoadMore?: () => void;
  onSetOffset?: (offset: number) => void;
  onNavigate?: (item?: IInvestment) => void;
  getFields: (item: IInvestment) => OfferingItem[];
}

const BrowsingGalleryView = ({
  items,
  promotion,
  activeOrders,
  offset,
  limit,
  total,
  showActions,
  alignStart,
  emptyTitle,
  emptyDescription,
  onLoadMore,
  onSetOffset,
  onNavigate,
  getFields
}: IBrowsingGalleryView) => {
  const assetComponents = items?.map((item: IInvestment, i: number) => (
    <BrowsingItem
      key={`item-${i}`}
      className={s.BrowsingGalleryView__item}
      item={item}
      showActions={showActions}
      onNavigate={onNavigate}
      getFields={getFields}
    />
  ));

  if (promotion) {
    assetComponents?.unshift(
      <FeaturedOffering
        key="FeaturedOffering"
        className={s.BrowsingGalleryView__featured}
        offeringInfo={promotion}
      />
    );
  }

  if (isNotEmpty(activeOrders)) {
    assetComponents?.unshift(
      <OrdersList
        key="OrdersList"
        className={s.BrowsingGalleryView__orderList}
        orders={activeOrders}
      />
    );
  }

  const moreItems = onLoadMore && total && items?.length && total > items?.length;
  const pagination = onSetOffset && total && total > 10;

  return (
    <>
      {isNotEmpty(items) && isNotEmpty(assetComponents) ? (
        <div className={s.BrowsingGalleryView}>
          <div className={cn(s.BrowsingGalleryView__grid, { alignStart })}> {assetComponents}</div>
          {!!moreItems && (
            <span className={s.BrowsingGalleryView__load_more} onClick={onLoadMore}>
              Load more
            </span>
          )}
          {!!pagination && (
            <Pagination
              limit={limit}
              offset={offset}
              total={total || items?.length || 0}
              onSetOffset={onSetOffset}
            />
          )}
        </div>
      ) : (
        <BrowsingViewNoResults title={emptyTitle} description={emptyDescription} />
      )}
    </>
  );
};

export default observer(BrowsingGalleryView);
